'use client';

import { useState, useEffect, useLayoutEffect } from 'react';
import SearchBar, { loadRecent, type SearchBarProps } from './SearchBar';
import { bumpPastDates, parseOccupancyCMS } from '../config';
import { hotelsClient } from '@bip/api-client';

const useMountEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Renders a placeholder during SSR, then mounts SearchBar on the client.
// Never wrap this component in <Suspense> — it manages its own SSR transition.
export default function SearchBarWrapper(props: SearchBarProps) {
  const [state, setState] = useState<{
    mounted: boolean;
    overrides: Partial<SearchBarProps>;
    barKey: number;
  }>({ mounted: false, overrides: {}, barKey: 0 });

  useMountEffect(() => {
    let cancelled = false;
    const overrides: Partial<SearchBarProps> = {};

    if (!props.initialDestId) {
      // Home page: restore last search from localStorage
      const recents = loadRecent();
      const last = recents[0];
      if (last?.dest_id) {
        const { checkin, checkout } = bumpPastDates(last.checkin, last.checkout);
        overrides.initialDestination = last._label || '';
        overrides.initialDestId     = last.dest_id;
        overrides.initialCheckin    = checkin;
        overrides.initialCheckout   = checkout;
        overrides.initialRooms      = parseOccupancyCMS(String(last.rooms), last.adults, last.children);
        overrides.initialRatecode   = last.ratecode || '';
      }
      setState({ mounted: true, overrides, barKey: 0 });
    } else if (!props.initialDestination) {
      // destId known but label missing: mount immediately, check localStorage for instant label,
      // then background-fetch and remount SearchBar if found.
      const match = loadRecent().find((r) => r.dest_id === props.initialDestId);
      if (match?._label) overrides.initialDestination = match._label;
      setState({ mounted: true, overrides, barKey: 0 });
      if (!overrides.initialDestination) {
        hotelsClient.getLocationLabel(props.initialDestId).then((label) => {
          if (!cancelled && label) {
            setState((prev) => ({ mounted: true, overrides: { initialDestination: label }, barKey: prev.barKey + 1 }));
          }
        }).catch(() => { /* leave bar as-is */ });
      }
    } else {
      setState({ mounted: true, overrides, barKey: 0 });
    }

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state.mounted) {
    return (
      <div
        className="sb-bar"
        style={{ minHeight: 64, opacity: 0.7, pointerEvents: 'none' }}
        aria-hidden="true"
      />
    );
  }

  return <SearchBar key={state.barKey} {...props} {...state.overrides} />;
}
